import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronDown, GitCompare } from "lucide-react";
import { ActionMenu, Badge, Button } from "@h2j-data-team/ui";
import type { QvdAnalysisRun } from "@repo/shared";

import { renderLink } from "@/components/router-link";
import { qvdAnalysisRunsListQueryOptions } from "@/features/qvd-analysis/api";
import { useFormatDate } from "@/hooks/use-format-date";

/** A run's own timestamp for ordering the picker: when it ended, else when it was queued. */
function runTime(run: QvdAnalysisRun): number {
  return Date.parse(run.finishedAt ?? run.createdAt);
}

/**
 * "Compare" entry point: the other finished runs of the same connection AND
 * scope, most recent first. Hidden when this run is not finished or there is
 * nothing comparable to pair it with.
 */
export function CompareQvdRunMenu({ run }: { run: QvdAnalysisRun }) {
  const { t } = useTranslation("qvd-analysis");
  const { formatDateTime } = useFormatDate();

  // The runs list has no status filter — narrow it here, same as the API's own
  // comparability guard (completed + done + same scope).
  // ponytail: single page (limit 100); with more runs on the connection the
  // oldest comparable ones drop off the picker — paginate if that ever bites.
  const { data } = useQuery(qvdAnalysisRunsListQueryOptions({ connectionId: run.connectionId }));

  const candidates = (data?.data ?? []).filter(
    (r) =>
      r.id !== run.id &&
      r.status === "completed" &&
      r.phase === "done" &&
      r.scopeContextKey === run.scopeContextKey,
  );
  if (run.status !== "completed" || run.phase !== "done" || candidates.length === 0) return null;

  // "previous" marks the newest run that is actually older than this one — the
  // list may also hold newer runs when an older one is open.
  const currentTime = runTime(run);
  const previousId = candidates.find((r) => runTime(r) < currentTime)?.id;

  return (
    <ActionMenu
      label={t("compare.menuLabel")}
      renderLink={renderLink}
      trigger={
        <Button variant="outline" size="sm">
          <GitCompare />
          {t("compare.button")}
          <ChevronDown />
        </Button>
      }
      items={candidates.map((candidate) => ({
        id: candidate.id,
        // A real link: it can be opened in a new tab.
        href: `/qvd-analyses/${run.id}/compare/${candidate.id}?tab=added`,
        label: (
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-mono">
              {formatDateTime(candidate.finishedAt ?? candidate.createdAt)}
            </span>
            {candidate.id === previousId && (
              <Badge variant="secondary">{t("compare.previousBadge")}</Badge>
            )}
          </span>
        ),
        meta: candidate.coverage && (
          <span className="font-mono text-xs tabular-nums text-faint">
            {t("compare.menuFiles", { count: candidate.coverage.footprintFileCount })}
          </span>
        ),
      }))}
    />
  );
}
